import { Plane, BedDouble, Car, Ticket } from "lucide-react";
import { SCENARIO_COLORS } from "./ScenarioCalendar";

const TYPE_ICON = { flight: Plane, hotel: BedDouble, car: Car, event: Ticket };
const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function coversDay(startDate, endDate, iso) {
  return startDate && iso >= startDate && iso <= (endDate || startDate);
}

export default function WeekView({ data, scenarios, hiddenIds, hoveredId }) {
  if (!data) {
    return <div className="border border-sand-200 p-12 text-center label-tag animate-pulse">Loading week…</div>;
  }
  const colorFor = (sid) => {
    const idx = scenarios.findIndex((s) => s.id === sid);
    return SCENARIO_COLORS[idx % SCENARIO_COLORS.length].hex;
  };
  const visibleScenarios = data.scenarios.filter((s) => !hiddenIds.has(s.id));
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="border-2 border-ink bg-surface" data-testid="scenario-week-view">
      <div className="grid grid-cols-7">
        {data.days.map((day, i) => {
          const iso = day.date;
          const dnum = parseInt(iso.slice(8, 10), 10);
          const isToday = iso === todayIso;
          const loc = day.location;
          const trips = data.trips.filter((t) => coversDay(t.start_date, t.end_date, iso));
          const ghosts = visibleScenarios.filter((s) => coversDay(s.start_date, s.end_date, iso));
          const events = (data.events || []).filter((e) => e.date === iso);
          return (
            <div key={iso} className={`min-h-[340px] border-r border-sand-200 last:border-r-0 flex flex-col ${isToday ? "bg-blue-50/40" : ""}`}
              data-testid={`week-day-${iso}`}>
              <div className={`px-2 py-2 border-b-2 ${isToday ? "border-ember" : "border-ink"} flex items-center justify-between gap-1`}>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-sand-500">{DAY_NAMES[i % 7]}</p>
                  <p className={`font-display font-medium text-xl leading-none ${isToday ? "text-ember" : ""}`}>{dnum}</p>
                </div>
                {loc && (
                  <span
                    className={`font-mono text-[10px] uppercase tracking-wide px-1.5 py-1 ${
                      loc.planned ? "text-ember border border-dashed border-ember/60" : "bg-sand-100 text-sand-600"
                    }`}
                    title={`${loc.name}${loc.airport ? ` (${loc.airport})` : ""}${loc.planned ? " — from planned flight" : ""}`}
                    data-testid={`week-location-${iso}`}>
                    {loc.airport || loc.code}{loc.planned ? "*" : ""}
                  </span>
                )}
              </div>

              <div className="p-1.5 space-y-1 flex-1">
                {trips.map((t) => (
                  <div key={t.id}
                    className={`text-[9px] font-mono uppercase tracking-wide px-1.5 py-1 leading-tight ${
                      t.status === "planned"
                        ? "text-ember border border-ember/50 planned-stripes"
                        : "bg-ember text-white"
                    }`}
                    title={`${t.name} (${t.status})`}
                    data-testid={`week-trip-${t.id}-${iso}`}>
                    {t.name}
                  </div>
                ))}

                {ghosts.map((s) => {
                  const c = colorFor(s.id);
                  const dim = hoveredId && hoveredId !== s.id;
                  const dloc = s.days?.[iso];
                  return (
                    <div key={s.id}
                      className="border border-dashed px-1.5 py-1 transition-opacity"
                      style={{ borderColor: c, opacity: dim ? 0.25 : 1 }}
                      title={`Scenario: ${s.name}${dloc ? ` — ${dloc.name || dloc.code}` : ""}`}
                      data-testid={`week-scenario-${s.id}-${iso}`}>
                      <p className="text-[9px] font-mono uppercase tracking-wide leading-tight truncate" style={{ color: c }}>{s.name}</p>
                      {dloc && (
                        <p className="text-[10px] font-mono font-semibold leading-tight" style={{ color: c }} data-testid={`week-scenario-loc-${s.id}-${iso}`}>
                          @ {dloc.code}
                        </p>
                      )}
                    </div>
                  );
                })}

                {events.map((e, j) => {
                  const Icon = TYPE_ICON[e.type] || Ticket;
                  const planned = e.status === "planned";
                  return (
                    <div key={j}
                      className={`flex items-start gap-1.5 px-1.5 py-1 border ${planned ? "border-dashed border-sand-300" : "border-sand-200"} bg-surface`}
                      data-testid={`week-event-${iso}-${j}`}>
                      <Icon size={11} className={`mt-0.5 shrink-0 ${planned ? "text-sand-400" : "text-ember"}`} />
                      <div className="min-w-0">
                        {e.time && <p className="font-mono text-[9px] text-sand-500 leading-tight">{e.time}</p>}
                        <p className="text-[10px] leading-tight break-words">{e.title}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
